import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AttachFile as AttachIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
  Preview as PreviewIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import {
  BUNDLE_BUILDER_SOURCE_LABEL,
  LOCKED_BUNDLE_BUILDER_SUBJECT,
} from '../catalogDefaults';
import { formatFileSize, formatMoney } from '../formatters';
import { formatPhoneForDisplay, getPhoneDigits } from '../sendValidation';

function BundleBuilderSendDialogs({
  bundleBuilderSendSummary,
  canShowFinalSendPreview,
  copyOfferEmailHtml,
  copyOfferEmailText,
  currentUser,
  finalEmailPreviewHtml,
  handleBundleAttachmentUpload,
  handlePrepareSendDryRun,
  handleSendBundleOffer,
  isAgentBeta,
  offerEmailPreview,
  offerPreviewOpen,
  previewCopyStatus,
  quote,
  removeBundleAttachment,
  resetSendValidation,
  sendAttachments,
  sendConfirmOpen,
  sendDryRunData,
  sendDryRunLoading,
  sendDryRunOpen,
  sendDryRunResult,
  sendLoading,
  sendResult,
  sendValidationErrors,
  setOfferPreviewOpen,
  setSendConfirmOpen,
  setSendDryRunData,
  setSendDryRunOpen,
  setSendDryRunResult,
  setSendResult,
  setSendValidationErrors,
}) {
  return (
    <>
      <Dialog
          open={offerPreviewOpen}
          onClose={() => setOfferPreviewOpen(false)}
          maxWidth="md"
          fullWidth
      >
          <DialogTitle>{isAgentBeta ? 'Prepare Bundle Builder Send' : 'Preview Offer Email'}</DialogTitle>
          <DialogContent dividers>
              <Stack spacing={2}>
                  <Alert severity="info">
                      {isAgentBeta
                          ? 'Bundle Builder Beta sends approved Home Services quote emails only. Agents cannot edit the email HTML or approved shell.'
                          : 'Admin preview. This does not change saved templates or the existing Send Offer flow.'}
                  </Alert>

                  {!offerEmailPreview.hasHomeServices && (
                      <Alert severity="warning">
                          No approved email preview is available for this selection yet.
                      </Alert>
                  )}

                  {offerEmailPreview.warnings.length > 0 && (
                      <Alert severity="warning">
                          <Stack spacing={0.5}>
                              {offerEmailPreview.warnings.map((warning) => (
                                  <Typography key={warning} variant="body2">
                                      {warning}
                                  </Typography>
                              ))}
                          </Stack>
                      </Alert>
                  )}

                  {offerEmailPreview.hasHomeServices && !isAgentBeta && (
                      <Paper
                          variant="outlined"
                          sx={{
                              p: { xs: 1, sm: 2 },
                              bgcolor: 'grey.50',
                              maxHeight: 620,
                              overflow: 'auto'
                          }}
                      >
                          <Box
                              sx={{ bgcolor: 'background.paper', p: { xs: 1, sm: 2 } }}
                              dangerouslySetInnerHTML={{ __html: offerEmailPreview.html }}
                          />
                      </Paper>
                  )}

                  {offerEmailPreview.hasHomeServices && isAgentBeta && !sendDryRunOpen && (
                      <Alert severity="info">
                          Build quote, then use Prepare Send to enter customer details, validate, and preview the fully merged customer email.
                      </Alert>
                  )}

                  {previewCopyStatus && (
                      <Alert severity={previewCopyStatus.includes('copied') ? 'success' : 'warning'}>
                          {previewCopyStatus}
                      </Alert>
                  )}

                  {sendDryRunOpen && (
                      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'background.paper' }}>
                          <Stack spacing={2}>
                              <Alert severity="info">
                                  Prepare Send validates the quote first. Email is sent only after the confirmation step.
                              </Alert>

                              <Box>
                                  <Typography variant="subtitle1" gutterBottom>
                                      Prepare Send
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                      Fill customer details, validate the locked Bundle Builder quote email, preview the final customer version, then send.
                                  </Typography>
                              </Box>

                              <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6}>
                                      <TextField
                                          label="Customer first name"
                                          value={sendDryRunData.customer_first_name}
                                          onChange={(event) => {
                                              setSendDryRunData((current) => ({ ...current, customer_first_name: event.target.value }));
                                              resetSendValidation();
                                          }}
                                          fullWidth
                                          required
                                          size="small"
                                          error={Boolean(sendValidationErrors.customer_first_name)}
                                          helperText={sendValidationErrors.customer_first_name || 'Letters, spaces, hyphens, and apostrophes only'}
                                      />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                      <TextField
                                          label="Customer full name"
                                          value={sendDryRunData.customer_full_name}
                                          onChange={(event) => {
                                              setSendDryRunData((current) => ({ ...current, customer_full_name: event.target.value }));
                                              resetSendValidation();
                                          }}
                                          fullWidth
                                          required
                                          size="small"
                                          error={Boolean(sendValidationErrors.customer_full_name)}
                                          helperText={sendValidationErrors.customer_full_name || 'Letters, spaces, hyphens, and apostrophes only'}
                                      />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                      <TextField
                                          label="Customer email"
                                          type="email"
                                          value={sendDryRunData.customer_email}
                                          onChange={(event) => {
                                              setSendDryRunData((current) => ({ ...current, customer_email: event.target.value }));
                                              resetSendValidation();
                                          }}
                                          fullWidth
                                          required
                                          size="small"
                                          error={Boolean(sendValidationErrors.customer_email)}
                                          helperText={sendValidationErrors.customer_email || 'Valid email address'}
                                      />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                      <TextField
                                          label="Customer phone"
                                          value={sendDryRunData.customer_phone}
                                          onChange={(event) => {
                                              const value = event.target.value.replace(/[^\d\s\-()]/g, '');
                                              if (getPhoneDigits(value).length <= 10) {
                                                  setSendDryRunData((current) => ({ ...current, customer_phone: value }));
                                              }
                                              resetSendValidation();
                                          }}
                                          fullWidth
                                          required
                                          size="small"
                                          inputProps={{
                                              maxLength: 16,
                                              inputMode: 'tel'
                                          }}
                                          error={Boolean(sendValidationErrors.customer_phone)}
                                          helperText={sendValidationErrors.customer_phone || '10 digits; spaces, dashes, and brackets are okay'}
                                      />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                      <TextField
                                          label="Subject line"
                                          value={LOCKED_BUNDLE_BUILDER_SUBJECT}
                                          fullWidth
                                          required
                                          size="small"
                                          disabled
                                          helperText="Locked for Bundle Builder Beta"
                                          sx={{
                                              '& .MuiInputBase-input.Mui-disabled': {
                                                  color: 'text.primary',
                                                  WebkitTextFillColor: 'unset'
                                              }
                                          }}
                                      />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                      <TextField
                                          label="Agent name"
                                          value={currentUser?.name || ''}
                                          fullWidth
                                          size="small"
                                          disabled
                                          error={Boolean(sendValidationErrors.agent_name)}
                                          helperText={sendValidationErrors.agent_name || 'From logged-in user profile'}
                                          sx={{
                                              '& .MuiInputBase-input.Mui-disabled': {
                                                  color: 'text.primary',
                                                  WebkitTextFillColor: 'unset'
                                              }
                                          }}
                                      />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                      <TextField
                                          label="Agent phone"
                                          value={currentUser?.phone ? formatPhoneForDisplay(currentUser.phone) : 'Not provided in profile'}
                                          fullWidth
                                          size="small"
                                          disabled
                                          error={Boolean(sendValidationErrors.agent_phone)}
                                          helperText={sendValidationErrors.agent_phone || 'Admin updates this in the user profile'}
                                          sx={{
                                              '& .MuiInputBase-input.Mui-disabled': {
                                                  color: 'text.primary',
                                                  WebkitTextFillColor: 'unset'
                                              }
                                          }}
                                      />
                                  </Grid>
                              </Grid>

                              <Paper variant="outlined" sx={{ p: 1.5 }}>
                                  <Stack spacing={1}>
                                      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
                                          <Box>
                                              <Typography variant="subtitle2">File attachments (optional)</Typography>
                                              <Typography variant="caption" color="text.secondary">
                                                  Up to 5 files, 25MB total. Same file types as Send Offer.
                                              </Typography>
                                          </Box>
                                          <Button component="label" variant="outlined" size="small" startIcon={<AttachIcon />} disabled={sendAttachments.length >= 5}>
                                              Attach Files
                                              <input
                                                  hidden
                                                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.zip"
                                                  type="file"
                                                  multiple
                                                  onChange={handleBundleAttachmentUpload}
                                              />
                                          </Button>
                                      </Box>
                                      {sendAttachments.length > 0 && (
                                          <Stack spacing={0.75}>
                                              {sendAttachments.map((file, index) => (
                                                  <Box key={`${file.name}-${index}`} display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                                      <Typography variant="body2">
                                                          {file.name} <Typography component="span" variant="caption" color="text.secondary">({formatFileSize(file.size)})</Typography>
                                                      </Typography>
                                                      <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => removeBundleAttachment(index)}>
                                                          Remove
                                                      </Button>
                                                  </Box>
                                              ))}
                                              <Typography variant="caption" color="text.secondary">
                                                  Total size: {formatFileSize(sendAttachments.reduce((sum, file) => sum + file.size, 0))}
                                              </Typography>
                                          </Stack>
                                      )}
                                  </Stack>
                              </Paper>

                              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                                  <Stack spacing={1}>
                                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
                                          <Typography variant="subtitle2">Product/source summary</Typography>
                                          <Chip size="small" color="primary" variant="outlined" label={bundleBuilderSendSummary.sourceLabel} />
                                      </Box>
                                      <Stack direction="row" gap={0.75} flexWrap="wrap" useFlexGap>
                                          {bundleBuilderSendSummary.includedProducts.length > 0
                                              ? bundleBuilderSendSummary.includedProducts.map((product) => (
                                                  <Chip key={product} size="small" label={product} />
                                              ))
                                              : <Chip size="small" label="No Home Services product selected" color="warning" variant="outlined" />}
                                      </Stack>
                                      {bundleBuilderSendSummary.unsupportedProducts.length > 0 && (
                                          <Box>
                                              <Typography variant="caption" color="text.secondary">
                                                  Quote-only selections excluded from customer email HTML:
                                              </Typography>
                                              <Stack direction="row" gap={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                                  {bundleBuilderSendSummary.unsupportedProducts.map((product) => (
                                                      <Chip key={`${product.type}-${product.name}`} size="small" variant="outlined" label={`${product.type}: ${product.name}`} />
                                                  ))}
                                              </Stack>
                                          </Box>
                                      )}
                                  </Stack>
                              </Paper>

                              {canShowFinalSendPreview ? (
                                  <Accordion disableGutters defaultExpanded={Boolean(sendDryRunResult?.success)}>
                                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                          <Typography variant="subtitle2">Final customer email preview</Typography>
                                      </AccordionSummary>
                                      <AccordionDetails>
                                          <Paper
                                              variant="outlined"
                                              sx={{
                                                  p: 1,
                                                  bgcolor: 'grey.50',
                                                  maxHeight: 320,
                                                  overflow: 'auto'
                                              }}
                                          >
                                              <Box
                                                  sx={{ bgcolor: 'background.paper', p: 1 }}
                                                  dangerouslySetInnerHTML={{ __html: finalEmailPreviewHtml }}
                                              />
                                          </Paper>
                                      </AccordionDetails>
                                  </Accordion>
                              ) : (
                                  <Alert severity="info">
                                      The final customer email preview appears after customer details validate. No placeholders will be shown to agents.
                                  </Alert>
                              )}

                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                  <Button
                                      variant="contained"
                                      startIcon={sendDryRunLoading ? <CircularProgress size={18} color="inherit" /> : <PreviewIcon />}
                                      onClick={handlePrepareSendDryRun}
                                      disabled={sendDryRunLoading || !offerEmailPreview.hasHomeServices}
                                  >
                                      {sendDryRunLoading ? 'Validating...' : 'Validate Send Details'}
                                  </Button>
                                  <Button
                                      variant="contained"
                                      color="success"
                                      startIcon={sendLoading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                                      onClick={() => setSendConfirmOpen(true)}
                                      disabled={sendLoading || !sendDryRunResult?.success || !offerEmailPreview.hasHomeServices}
                                  >
                                      Send Email
                                  </Button>
                              </Stack>

                              {sendDryRunResult && (
                                  <Alert severity={sendDryRunResult.success ? 'success' : 'error'}>
                                      <Stack spacing={1}>
                                          <Typography variant="subtitle2">
                                              {sendDryRunResult.success ? 'Validation passed. Review the preview, then send.' : 'Validation needs attention.'}
                                          </Typography>
                                          <Typography variant="body2">
                                              Source: {sendDryRunResult.proposedSourceLabel || BUNDLE_BUILDER_SOURCE_LABEL}
                                          </Typography>
                                          <Typography variant="body2">
                                              Subject: {sendDryRunResult.proposedSubject || LOCKED_BUNDLE_BUILDER_SUBJECT}
                                          </Typography>
                                          {sendDryRunResult.emailProvider && (
                                              <Typography variant="body2">
                                                  Email provider: {sendDryRunResult.emailProvider.provider?.toUpperCase()} ({sendDryRunResult.emailProvider.configured ? 'configured' : 'not configured'})
                                              </Typography>
                                          )}
                                          {sendDryRunResult.errors?.length > 0 && (
                                              <Box>
                                                  <Typography variant="body2" fontWeight={700}>Errors</Typography>
                                                  <List dense sx={{ py: 0 }}>
                                                      {sendDryRunResult.errors.map((error) => (
                                                          <ListItem key={error} disableGutters sx={{ py: 0 }}>
                                                              <ListItemText primary={error} primaryTypographyProps={{ variant: 'body2' }} />
                                                          </ListItem>
                                                      ))}
                                                  </List>
                                              </Box>
                                          )}
                                          {sendDryRunResult.warnings?.length > 0 && (
                                              <Box>
                                                  <Typography variant="body2" fontWeight={700}>Warnings</Typography>
                                                  <List dense sx={{ py: 0 }}>
                                                      {sendDryRunResult.warnings.map((warning) => (
                                                          <ListItem key={warning} disableGutters sx={{ py: 0 }}>
                                                              <ListItemText primary={warning} primaryTypographyProps={{ variant: 'body2' }} />
                                                          </ListItem>
                                                      ))}
                                                  </List>
                                              </Box>
                                          )}
                                      </Stack>
                                  </Alert>
                              )}

                              {sendResult && (
                                  <Alert severity={sendResult.success ? 'success' : 'error'}>
                                      {sendResult.message}
                                      {sendResult.sentOfferId ? ` History ID: ${sendResult.sentOfferId}` : ''}
                                  </Alert>
                              )}
                          </Stack>
                      </Paper>
                  )}
              </Stack>
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, py: 2 }}>
              {Boolean(currentUser?.isAdmin) && !isAgentBeta && (
                  <>
                      <Button startIcon={<CopyIcon />} variant="contained" onClick={copyOfferEmailHtml} disabled={!offerEmailPreview.hasHomeServices}>
                          Copy Email HTML
                      </Button>
                      <Button startIcon={<CopyIcon />} variant="outlined" onClick={copyOfferEmailText} disabled={!offerEmailPreview.hasHomeServices}>
                          Copy Plain Text Summary
                      </Button>
                  </>
              )}
              {!isAgentBeta && (
                  <Button
                      startIcon={<EmailIcon />}
                      variant={sendDryRunOpen ? 'contained' : 'outlined'}
                      color="secondary"
                      onClick={() => {
                          setSendDryRunOpen((current) => !current);
                          setSendDryRunResult(null);
                          setSendResult(null);
                          setSendValidationErrors({});
                      }}
                      disabled={!offerEmailPreview.hasHomeServices}
                  >
                      Prepare Send
                  </Button>
              )}
              <Button onClick={() => setOfferPreviewOpen(false)}>
                  Close
              </Button>
          </DialogActions>
      </Dialog>

      <Dialog
          open={sendConfirmOpen}
          onClose={() => !sendLoading && setSendConfirmOpen(false)}
          maxWidth="sm"
          fullWidth
      >
          <DialogTitle>Send Bundle Builder Quote?</DialogTitle>
          <DialogContent dividers>
              <Stack spacing={1.5}>
                  <Alert severity="warning">
                      Send this Bundle Builder quote to {sendDryRunData.customer_email || 'the customer'}?
                  </Alert>
                  <Typography variant="body2">
                      <strong>Source:</strong> {BUNDLE_BUILDER_SOURCE_LABEL}
                  </Typography>
                  <Typography variant="body2">
                      <strong>Subject:</strong> {LOCKED_BUNDLE_BUILDER_SUBJECT}
                  </Typography>
                  <Typography variant="body2">
                      <strong>Monthly total:</strong> {formatMoney(quote.monthlySubtotal)}
                  </Typography>
                  <Typography variant="body2">
                      <strong>Selected Home Services:</strong> {bundleBuilderSendSummary.includedProducts.join(', ') || 'None'}
                  </Typography>
                  <Typography variant="body2">
                      <strong>From:</strong> {currentUser?.name || 'Logged-in agent'} {currentUser?.phone ? `(${formatPhoneForDisplay(currentUser.phone)})` : ''}
                  </Typography>
                  {sendAttachments.length > 0 && (
                      <Typography variant="body2">
                          <strong>Attachments:</strong> {sendAttachments.length} file(s), {formatFileSize(sendAttachments.reduce((sum, file) => sum + file.size, 0))}
                      </Typography>
                  )}
              </Stack>
          </DialogContent>
          <DialogActions>
              <Button onClick={() => setSendConfirmOpen(false)} disabled={sendLoading}>
                  Cancel
              </Button>
              <Button
                  variant="contained"
                  color="success"
                  startIcon={sendLoading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                  onClick={handleSendBundleOffer}
                  disabled={sendLoading}
              >
                  {sendLoading ? 'Sending...' : 'Send Email'}
              </Button>
          </DialogActions>
      </Dialog>
    </>
  );
}

export default BundleBuilderSendDialogs;
